import { HCO_HIERARCHY_CUBE_NAME, HCP_HCO_CUBE_NAME } from '@/api/hco/constants';
import { useAllowlistedGlobalFilters } from '@/utils/cube/useGetGlobalFilters';
import { Filter, LogicalAndFilter } from '@cubejs-client/core';

export type DepartmentCountScopeShape = 'hco-only' | 'all-departments';

const PARENT_HCO_BK_MEMBER = `${HCO_HIERARCHY_CUBE_NAME}.parent_hco_bk`;

export const DEPT_HCP_BRIDGE_SET_FILTER: Filter = {
  member: `${HCP_HCO_CUBE_NAME}.hco_bk`,
  operator: 'set',
};

export function buildDeptHcoOnlyFilters(hcoBk: string): Filter[] {
  return [
    { member: PARENT_HCO_BK_MEMBER, operator: 'equals', values: [hcoBk] },
    { member: `${HCO_HIERARCHY_CUBE_NAME}.path_mod`, operator: 'contains', values: [hcoBk] },
  ];
}

export function buildDeptAllDepartmentsFilters(hcoBk: string): Filter[] {
  return [
    { member: `${HCO_HIERARCHY_CUBE_NAME}.hco_bk`, operator: 'notEquals', values: [hcoBk] },
    { member: `${HCO_HIERARCHY_CUBE_NAME}.path_mod`, operator: 'contains', values: [hcoBk] },
  ];
}

export function buildDeptFilters(hcoBk: string | undefined, includeAllDepartments: boolean): Filter[] {
  if (!hcoBk) return [];
  if (includeAllDepartments) return buildDeptAllDepartmentsFilters(hcoBk);
  return buildDeptHcoOnlyFilters(hcoBk);
}

export function stripRootParentHcoBkFilter(deptFilters: Filter[], hcoBk: string): Filter[] {
  return deptFilters.filter(scopeFilter => {
    if (
      !('member' in scopeFilter) ||
      scopeFilter.member !== PARENT_HCO_BK_MEMBER ||
      scopeFilter.operator !== 'equals'
    ) {
      return true;
    }
    if (scopeFilter.values.length !== 1) {
      return true;
    }
    return scopeFilter.values[0] !== hcoBk;
  });
}

function applyHcoOnlyGlobalDepartmentScope(
  deptFilters: Filter[],
  hcoBk: string,
  hasActiveDeptGlobalFilters: boolean,
  hasActiveHcpGlobalFilters: boolean,
): Filter[] {
  if (!hasActiveDeptGlobalFilters || hasActiveHcpGlobalFilters) {
    return deptFilters;
  }
  return stripRootParentHcoBkFilter(deptFilters, hcoBk);
}

export function buildDepartmentCountScopeFilters(
  deptFilters: Filter[],
  hasActiveDeptGlobalFilters: boolean,
  hasActiveHcpGlobalFilters: boolean,
  deptScopeShape: DepartmentCountScopeShape,
  hcoBk: string,
): Filter[] {
  const scopedDeptFilters = applyHcoOnlyGlobalDepartmentScope(
    deptFilters,
    hcoBk,
    hasActiveDeptGlobalFilters,
    hasActiveHcpGlobalFilters,
  );

  if (!hasActiveDeptGlobalFilters) return scopedDeptFilters;
  if (hasActiveHcpGlobalFilters) {
    if (deptScopeShape === 'all-departments') {
      return [...scopedDeptFilters, DEPT_HCP_BRIDGE_SET_FILTER];
    }
    return [{ and: scopedDeptFilters }, DEPT_HCP_BRIDGE_SET_FILTER];
  }
  return [...scopedDeptFilters];
}

export function buildDepartmentCountQueryFilters(
  deptScopeFilters: Filter[],
  deptGlobalFilters: LogicalAndFilter,
  hasActiveDeptGlobalFilters: boolean,
  hasActiveHcpGlobalFilters: boolean,
  deptScopeShape: DepartmentCountScopeShape,
  hcoBk: string,
): Filter[] {
  const scopedFilters = buildDepartmentCountScopeFilters(
    deptScopeFilters,
    hasActiveDeptGlobalFilters,
    hasActiveHcpGlobalFilters,
    deptScopeShape,
    hcoBk,
  );
  return hasActiveDeptGlobalFilters ? [deptGlobalFilters, ...scopedFilters] : scopedFilters;
}

export function buildHierarchyChildDepartmentCountFilters(
  childDeptScopeFilters: Filter[],
  deptGlobalFilters: LogicalAndFilter,
  hasActiveDeptGlobalFilters: boolean,
  hasActiveHcpGlobalFilters: boolean,
): Filter[] {
  if (!hasActiveDeptGlobalFilters) return childDeptScopeFilters;
  if (hasActiveHcpGlobalFilters) {
    return [deptGlobalFilters, ...childDeptScopeFilters, DEPT_HCP_BRIDGE_SET_FILTER];
  }
  return [deptGlobalFilters, ...childDeptScopeFilters];
}

export interface DepartmentCountGlobalFilterState {
  deptGlobalFilters: LogicalAndFilter;
  hasActiveDeptGlobalFilters: boolean;
  hasActiveHcpGlobalFilters: boolean;
}

export function useDepartmentCountGlobalFilterState(includeAllDepartments: boolean): DepartmentCountGlobalFilterState {
  const deptGlobalFilters = useAllowlistedGlobalFilters({
    includeHco: includeAllDepartments,
    excludeDisabled: false,
  });
  const hcpGlobalFilters = useAllowlistedGlobalFilters({
    includeHco: false,
    excludeDisabled: false,
  });

  return {
    deptGlobalFilters,
    hasActiveDeptGlobalFilters: deptGlobalFilters.and.length > 0,
    hasActiveHcpGlobalFilters: hcpGlobalFilters.and.length > 0,
  };
}
